import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const SITE_URL = 'https://sakk.app'
const BASE_PATH = '/'
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`

interface SEOData {
  title?: string
  description?: string
  ogImage?: string
  ogType?: string
  article?: {
    publishedTime?: string
    modifiedTime?: string
    author?: string
    section?: string
    tags?: string[]
  }
  noindex?: boolean
}

function setMeta(name: string, content: string, property = false) {
  const attr = property ? 'property' : 'name'
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`
  let el = document.querySelector(selector) as HTMLLinkElement
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    if (hreflang) el.setAttribute('hreflang', hreflang)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function setStructuredData(id: string, data: object) {
  let el = document.querySelector(`script[data-seo-id="${id}"]`)
  if (!el) {
    el = document.createElement('script')
    el.setAttribute('type', 'application/ld+json')
    el.setAttribute('data-seo-id', id)
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

export function useSEO(seo: SEOData = {}) {
  const { pathname } = useLocation()
  const { i18n } = useTranslation()
  const lang = i18n.language
  const isAr = lang === 'ar'

  useEffect(() => {
    const fullUrl = `${SITE_URL}${BASE_PATH}${pathname}`

    // Title
    const baseTitle = isAr ? 'محفظة صك' : 'SAKK Wallet'
    document.title = seo.title ? `${seo.title} | ${baseTitle}` : baseTitle

    // Basic meta
    setMeta('description', seo.description || (isAr
      ? 'محفظة صك — بطاقات افتراضية، تحويلات فورية، تجارة الذهب، وأكثر من 135 عملة. دفع رقمي آمن ومريح.'
      : 'SAKK Wallet — Virtual cards, instant transfers, gold trading, and 135+ currencies. Secure and convenient digital payments.'
    ))

    // Open Graph
    setMeta('og:title', seo.title ? `${seo.title} | ${baseTitle}` : baseTitle, true)
    setMeta('og:description', seo.description || (isAr
      ? 'محفظة صك — بطاقات افتراضية، تحويلات فورية، تجارة الذهب، وأكثر من 135 عملة.'
      : 'SAKK Wallet — Virtual cards, instant transfers, gold trading, and 135+ currencies.'
    ), true)
    setMeta('og:url', fullUrl, true)
    setMeta('og:type', seo.ogType || 'website', true)
    setMeta('og:site_name', 'SAKK Wallet', true)
    setMeta('og:locale', isAr ? 'ar_SY' : 'en_US', true)
    setMeta('og:image', seo.ogImage || DEFAULT_OG_IMAGE, true)
    setMeta('og:image:width', '1200', true)
    setMeta('og:image:height', '630', true)
    setMeta('og:image:alt', seo.title || (isAr ? 'محفظة صك' : 'SAKK Wallet'), true)
    if (!isAr) setMeta('og:locale:alternate', 'ar_SY', true)

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image')
    setMeta('twitter:title', seo.title ? `${seo.title} | ${baseTitle}` : baseTitle)
    setMeta('twitter:description', seo.description || (isAr
      ? 'محفظة صك — بطاقات افتراضية، تحويلات فورية، تجارة الذهب.'
      : 'SAKK Wallet — Virtual cards, instant transfers, gold trading.'
    ))
    setMeta('twitter:image', seo.ogImage || DEFAULT_OG_IMAGE)

    // Canonical + hreflang
    setLink('canonical', fullUrl)
    setLink('alternate', fullUrl, 'ar')
    setLink('alternate', fullUrl, 'en')
    setLink('alternate', fullUrl, 'x-default')

    // Robots
    if (seo.noindex) {
      setMeta('robots', 'noindex, nofollow')
    } else {
      setMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1')
    }

    // Article meta
    if (seo.article) {
      if (seo.article.publishedTime) setMeta('article:published_time', seo.article.publishedTime, true)
      if (seo.article.modifiedTime) setMeta('article:modified_time', seo.article.modifiedTime, true)
      if (seo.article.author) setMeta('article:author', seo.article.author, true)
      if (seo.article.section) setMeta('article:section', seo.article.section, true)
      if (seo.article.tags) {
        seo.article.tags.forEach(tag => setMeta('article:tag', tag, true))
      }
    }

    // Language
    document.documentElement.lang = lang
    document.documentElement.dir = isAr ? 'rtl' : 'ltr'
  }, [seo, pathname, i18n.language])
}

/* ═══════════════════════════════════════════════════════
   SCHEMA: Organization (homepage only)
   ═══════════════════════════════════════════════════════ */
export function useOrganizationSchema() {
  useEffect(() => {
    setStructuredData('organization', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'SAKK',
      alternateName: 'صك',
      url: 'https://sakk.app',
      logo: 'https://sakk.app/logo.png',
      description: 'Digital wallet with virtual cards, instant transfers, gold trading, and multi-currency support for 135+ currencies',
      sameAs: [
        'https://twitter.com/sakkwallet',
        'https://www.facebook.com/sakkwallet',
        'https://www.instagram.com/sakkwallet'
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'support@sakk.app',
        availableLanguage: ['Arabic', 'English']
      },
      foundingDate: '2024',
      areaServed: {
        '@type': 'GeoCircle',
        name: 'Worldwide'
      }
    })
  }, [])
}

/* ═══════════════════════════════════════════════════════
   SCHEMA: WebSite (homepage only)
   ═══════════════════════════════════════════════════════ */
export function useWebsiteSchema() {
  useEffect(() => {
    setStructuredData('website', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'SAKK Wallet',
      alternateName: 'محفظة صك',
      url: 'https://sakk.app',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://sakk.app/blog?q={search_term_string}',
        'query-input': 'required name=search_term_string'
      },
      inLanguage: ['ar', 'en'],
      about: {
        '@type': 'Thing',
        name: 'Digital Wallet',
        alternateName: 'محفظة رقمية'
      }
    })
  }, [])
}

/* ═══════════════════════════════════════════════════════
   SCHEMA: WebPage (generic pages)
   ═══════════════════════════════════════════════════════ */
export function useWebPageSchema(title: string, description: string, type = 'WebPage') {
  useEffect(() => {
    setStructuredData('webpage', {
      '@context': 'https://schema.org',
      '@type': type,
      name: title,
      description: description,
      url: `${SITE_URL}${BASE_PATH}${window.location.pathname}`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'SAKK Wallet',
        url: 'https://sakk.app'
      },
      inLanguage: document.documentElement.lang,
      publisher: {
        '@type': 'Organization',
        name: 'SAKK',
        logo: {
          '@type': 'ImageObject',
          url: 'https://sakk.app/logo.png'
        }
      }
    })
  }, [title, description, type])
}

/* ═══════════════════════════════════════════════════════
   SCHEMA: FAQPage (AEO — voice search & answer boxes)
   Add this to any page that has FAQ content.
   ═══════════════════════════════════════════════════════ */
interface FAQItem {
  question: string
  answer: string
}

export function useFAQSchema(items: FAQItem[]) {
  useEffect(() => {
    if (!items.length) return
    setStructuredData('faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    })
    return () => {
      const el = document.querySelector('script[data-seo-id="faq"]')
      if (el) el.remove()
    }
  }, [items])
}

/* ═══════════════════════════════════════════════════════
   SCHEMA: Article (blog posts — SEO + AEO + GEO)
   ═══════════════════════════════════════════════════════ */
interface ArticleData {
  title: string
  description: string
  slug: string
  publishedTime: string
  modifiedTime?: string
  author?: string
  section?: string
  tags?: string[]
  image?: string
}

export function useArticleSchema(article: ArticleData) {
  useEffect(() => {
    const url = `${SITE_URL}${BASE_PATH}/blog/${article.slug}`
    setStructuredData('article', {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.description,
      url: url,
      image: article.image || DEFAULT_OG_IMAGE,
      datePublished: article.publishedTime,
      dateModified: article.modifiedTime || article.publishedTime,
      author: {
        '@type': 'Organization',
        name: 'SAKK',
        url: 'https://sakk.app'
      },
      publisher: {
        '@type': 'Organization',
        name: 'SAKK',
        logo: {
          '@type': 'ImageObject',
          url: 'https://sakk.app/logo.png'
        }
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url
      },
      articleSection: article.section || 'Finance',
      keywords: article.tags?.join(', ') || '',
      inLanguage: document.documentElement.lang,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', '.article-body p:first-of-type']
      }
    })
  }, [article])
}

/* ═══════════════════════════════════════════════════════
   SCHEMA: SoftwareApplication (app download page)
   ═══════════════════════════════════════════════════════ */
export function useSoftwareApplicationSchema() {
  useEffect(() => {
    setStructuredData('software-app', {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'SAKK Wallet',
      alternateName: 'محفظة صك',
      operatingSystem: 'Android, iOS',
      applicationCategory: 'FinanceApplication',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        ratingCount: '50000',
        bestRating: '5'
      },
      description: 'Digital wallet with virtual cards, instant transfers, gold trading, and multi-currency support for 135+ currencies',
      url: 'https://sakk.app',
      screenshot: 'https://sakk.app/og-image.png',
      featureList: [
        'Virtual Visa/Mastercard cards',
        'Instant P2P transfers',
        'Gold trading by the gram',
        '135+ currency support',
        'Agent cash-in/cash-out network',
        '2FA & biometric security'
      ],
      softwareVersion: '1.0',
      fileSize: '15MB',
      permissions: 'Camera, Biometric, Internet',
      applicationSubCategory: 'Mobile Wallet, Digital Payments'
    })
  }, [])
}

/* ═══════════════════════════════════════════════════════
   SCHEMA: BreadcrumbList (all sub-pages)
   ═══════════════════════════════════════════════════════ */
interface BreadcrumbItem {
  name: string
  url: string
}

export function useBreadcrumbSchema(items: BreadcrumbItem[]) {
  useEffect(() => {
    setStructuredData('breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.name,
        item: `${SITE_URL}${BASE_PATH}${item.url}`
      }))
    })
  }, [items])
}
